import { useEffect, useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { Pencil, Copy, Check } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { AppShell } from '@/components/AppShell';
import { AlbumCoverCard } from '@/components/AlbumCoverCard';
import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { EditProfileForm } from '@/components/EditProfileForm';
import { FollowButton } from '@/components/FollowButton';
import { IridescentAvatar } from '@/components/IridescentAvatar';
import { PolaroidFeed } from '@/components/PolaroidFeed';
import { PhotoDetail } from '@/components/PhotoDetail';
import { DateDoodle } from '@/components/DateDoodle';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useDittoTheme } from '@/hooks/useDittoTheme';
import { useUserAlbums } from '@/hooks/useUserAlbums';
import { useUserPicturesInfinite } from '@/hooks/useUserPicturesInfinite';
import { getAvatarShape } from '@/lib/avatarShape';
import { genUserName } from '@/lib/genUserName';
import { getPastelStripColor, seedFromHex, getTapeVariant } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';

/** Ruled notebook lines background */
const RULED_LINES = 'repeating-linear-gradient(to bottom, transparent, transparent 19px, rgba(180,200,220,0.25) 19px, rgba(180,200,220,0.25) 20px)';

/** Tape solid colors — CSS variables that adapt to theme */
const TAPE_SOLID_VARS = [
  'var(--tape-0-solid)', 'var(--tape-1-solid)', 'var(--tape-2-solid)', 'var(--tape-3-solid)',
  'var(--tape-4-solid)', 'var(--tape-5-solid)', 'var(--tape-6-solid)', 'var(--tape-7-solid)',
];

/** Displays truncated npub with a copy-npub button */
function CopyableNpub({ npub, truncatedNpub, hasBgImage, pastelColor }: {
  npub: string;
  truncatedNpub: string;
  hasBgImage: boolean;
  pastelColor: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(npub);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = npub;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="mt-0.5">
      <div className="flex items-center gap-1">
        <p
          className={cn('font-hand text-[13px] text-pol-text-secondary/70', hasBgImage ? 'paper-strip' : 'paper-strip-pastel')}
          style={hasBgImage ? undefined : { backgroundColor: pastelColor }}
        >
          {truncatedNpub}
        </p>
        <button
          onClick={handleCopy}
          className={cn(
            'flex-shrink-0 text-pol-text-secondary/50 hover:text-pol-text-secondary transition-colors active:scale-90 p-1',
            hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
          )}
          style={hasBgImage ? undefined : { backgroundColor: pastelColor }}
          aria-label="Copy npub"
          title="Copy npub"
        >
          {copied ? <Check size={14} className="text-pol-mint" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}

interface ProfileContentProps {
  pubkey: string;
}

export function ProfileContent({ pubkey }: ProfileContentProps) {
  const author = useAuthor(pubkey);
  const { data, isLoading: picturesLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useUserPicturesInfinite(pubkey);
  const { data: albums } = useUserAlbums(pubkey);
  const { user } = useCurrentUser();
  const { data: profileTheme } = useDittoTheme(pubkey);
  const { setOverrideTheme, setForceDefault, hasBgImage } = useDittoThemeContext();
  const [selectedPhoto, setSelectedPhoto] = useState<NostrEvent | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // When visiting someone else's profile, apply their theme as an override.
  // When visiting your own profile, no override — the global user theme applies.
  // When the visited user has NO theme, force the default so the logged-in
  // user's theme doesn't bleed through.
  const isOwnProfile = user?.pubkey === pubkey;

  useEffect(() => {
    if (!isOwnProfile && profileTheme) {
      setOverrideTheme(profileTheme);
    } else if (!isOwnProfile && !profileTheme) {
      // The visited user has no theme — force default so the logged-in user's
      // theme doesn't show on someone else's profile
      setForceDefault(true);
    }

    return () => {
      // Always clear on unmount so other pages revert to the logged-in user's theme
      setOverrideTheme(undefined);
      setForceDefault(false);
    };
  }, [isOwnProfile, profileTheme, setOverrideTheme, setForceDefault]);

  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(pubkey);

  // Parse avatar shape from raw kind-0 content (custom `shape` property)
  const avatarShape = useMemo(() => {
    const event = author.data?.event;
    if (!event) return undefined;
    try {
      const parsed = JSON.parse(event.content);
      return getAvatarShape(parsed);
    } catch {
      return undefined;
    }
  }, [author.data?.event]);
  const npub = nip19.npubEncode(pubkey);
  const truncatedNpub = `${npub.slice(0, 12)}...${npub.slice(-6)}`;

  // Flatten pages and deduplicate by event ID
  const pictures = useMemo(() => {
    const seen = new Set<string>();
    return (data?.pages.flat() ?? []).filter((event) => {
      if (seen.has(event.id)) return false;
      seen.add(event.id);
      return true;
    });
  }, [data?.pages]);

  const pageCount = data?.pages.length ?? 0;

  useSeoMeta({
    title: `${displayName} — Polaroids`,
    description: metadata?.about ?? `${displayName}'s photo album on Polaroids`,
  });

  return (
    <AppShell>
      <div className="py-6">
        {/* Profile header */}
        <div className="mb-6 relative">
          <div className="flex items-center gap-4">
            <IridescentAvatar
              src={metadata?.picture}
              alt={displayName}
              size={80}
              shape={avatarShape}
              themeColor={profileTheme?.colors.background}
            />

            <div className="min-w-0 flex-1">
              <h1
                className={cn('font-hand font-bold text-2xl text-pol-text', hasBgImage ? 'paper-strip' : 'paper-strip-pastel')}
                style={hasBgImage ? undefined : { backgroundColor: getPastelStripColor(pubkey + 'name') }}
              >
                {displayName}
              </h1>

              <CopyableNpub
                npub={npub}
                truncatedNpub={truncatedNpub}
                hasBgImage={hasBgImage}
                pastelColor={getPastelStripColor(pubkey + 'nip05')}
              />
            </div>

            {/* Edit button — own profile only */}
            {isOwnProfile && (
              <button
                onClick={() => setEditOpen(true)}
                className={cn(
                  'flex-shrink-0 self-start mt-1 w-9 h-9 flex items-center justify-center text-pol-text-secondary hover:text-pol-text transition-colors active:scale-90',
                  hasBgImage && 'paper-strip',
                )}
                aria-label="Edit profile"
              >
                <Pencil size={18} />
              </button>
            )}

            {/* Follow button — other profiles only */}
            {!isOwnProfile && (
              <FollowButton pubkey={pubkey} className="flex-shrink-0 self-start mt-1" />
            )}
          </div>

          {metadata?.about && (() => {
            const bioRotation = (seedFromHex(pubkey, 20, 24) % 5) - 2;
            const bioTapeIdx = getTapeVariant(pubkey);
            const bioTapeColor = TAPE_SOLID_VARS[bioTapeIdx % TAPE_SOLID_VARS.length];
            const bioTapeRotation = (seedFromHex(pubkey, 6, 8) % 10) - 5;

            return (
              <div
                className="mt-3 max-w-[420px]"
                style={{ transform: `rotate(${bioRotation}deg)` }}
              >
                <div className="relative">
                  {/* Tape strip — horizontal */}
                  <div
                    className="absolute left-1/2 z-10"
                    style={{
                      width: '80px',
                      height: '20px',
                      top: '-10px',
                      backgroundColor: bioTapeColor,
                      opacity: 0.7,
                      transform: `translateX(-50%) rotate(${bioTapeRotation}deg)`,
                    }}
                  />

                  {/* Notebook paper body */}
                  <div
                    className="relative px-4 pt-5 pb-4"
                    style={{
                      backgroundColor: '#FFFFFF',
                      backgroundImage: RULED_LINES,
                      backgroundPosition: '0 10px',
                      boxShadow: '1px 2px 6px rgba(0,0,0,0.08)',
                    }}
                  >
                    <p
                      className="font-hand text-base leading-[20px] break-words overflow-hidden line-clamp-6"
                      style={{ color: '#2D1B4E', overflowWrap: 'anywhere' }}
                    >
                      {metadata.about}
                    </p>
                  </div>

                  {/* Peeling corner */}
                  <div
                    className="absolute bottom-0 right-0 overflow-hidden"
                    style={{ width: '20px', height: '20px' }}
                  >
                    <div className="absolute inset-0" style={{ backgroundColor: '#FDF6EC' }} />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, transparent 50%, #E8E8E8 50%)' }} />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, transparent 45%, rgba(0,0,0,0.08) 50%, transparent 55%)' }} />
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Album row — horizontal scroll of album covers */}
        {albums && albums.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <DateDoodle seed={pubkey + 'albums-label'} size={18} />
              <span
                className={cn(
                  'font-hand font-bold text-lg text-pol-text',
                  hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
                )}
                style={hasBgImage ? undefined : { backgroundColor: getPastelStripColor(pubkey + 'albums') }}
              >
                Albums
              </span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {albums.map((album, i) => (
                <div key={album.dTag} className="flex-shrink-0 w-[130px]">
                  <AlbumCoverCard album={album} variant="grid" index={i} />
                </div>
              ))}
            </div>
            {/* Dotted divider */}
            <div className="mt-3 border-b-2 border-dotted border-pol-text/10" />
          </div>
        )}

        {/* Photo feed — infinite scroll with embellishments */}
        <PolaroidFeed
          pictures={pictures}
          isLoading={picturesLoading}
          hasNextPage={!!hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          pageCount={pageCount}
          onPhotoClick={setSelectedPhoto}
          emptyState={
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="bg-polaroid-white rounded-[3px] p-4 pb-12 border-2 border-dashed border-cream-dark shadow-sm w-44 mx-auto" style={{ transform: 'rotate(-2deg)' }}>
                <div className="aspect-square flex items-center justify-center bg-cream-dark/50 rounded-sm">
                  <span className="text-3xl">📷</span>
                </div>
                <p className="font-hand text-pol-text-secondary text-center mt-2">No polaroids yet</p>
              </div>
            </div>
          }
        />
      </div>

      {/* Photo detail overlay */}
      {selectedPhoto && (
        <PhotoDetail
          event={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}

      {/* Edit profile modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md p-0 gap-0 bg-polaroid-white">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="font-hand text-2xl text-pol-text">Edit Profile</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] px-5 pb-5">
            <EditProfileForm />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
